import React, {
  ComponentType, memo,
  useCallback,
  useEffect,
  useMemo, useRef,
  useState,
} from 'react';
import {
  IconButton,
  InputBase,
  Stack,
} from '@mui/material';
import Box from '@mui/material/Box';
import { datagridColors } from '../../assets/datagridStyles';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridFooter,
  GridPaginationModel,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { Header } from '../AdminPages/Header';
import { useGridApiRef} from '@mui/x-data-grid';
import {styled, useTheme} from '@mui/material/styles';
import _, { debounce } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import api from '../../services/api';
import {
  CheckRounded, ClearRounded, DownloadRounded,
  FilterAltOutlined,
  FilterList, ScheduleSend, ScheduleSendRounded
} from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useStateContext} from "../../hooks/useStateContext";
import { toISODateString } from '../../services/toISODateString';
import {hasPermissionByPriority} from "../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../core/permissions/permissions";
export const ReservationSummaries: ComponentType = () => {
  const themeQuery = useTheme();
  const isMobile = useMediaQuery(themeQuery.breakpoints.down("lg"));
  const [loading, setLoading] = useState(false);
  const [addNewEntity, setAddNewEntity] = useState(false);
  const [rows, setRows] = useState<any>([]);
  const apiRef = useGridApiRef();
  const [fastSearchValue, setFastSearchValue] = useState<any>('');
  const [rowCount, setRowCount] = useState<any>(0);
  const [dateFilter, setDateFilter] = useState<any>(['', '']);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });
  const [openHeaderFilters, setOpenHeaderFilters] = useState(false);
  const [openFilters, setOpenFilters] = useState(!isMobile);

  const [filterModelStatic, setFilterModelStatic] = useState<GridFilterModel>({items: []});
  const [sortModelStatic, setSortModelStatic] = useState<GridSortModel>([]);
  const [paginationModelStatic, setPaginationModelStatic] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [userFilterOptions, setUserFilterOptions] = useState<any>([]);
  const [filters, setFilters] = useState({});
  const autosizeOptions = {
    includeHeaders: true,
    includeOutliers: true,
    outliersFactor: 1.5,
    expand: true,
  };
  const { viewPeriod } = useStateContext();

  const {user} = useStateContext();
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);

  const fetchLogs = useCallback(() => {
    setLoading(true);

    const searchParams = new URLSearchParams();
    searchParams.set('page', (paginationModelStatic.page + 1).toString());
    searchParams.set('pageSize', paginationModelStatic.pageSize.toString());
    searchParams.set('from', viewPeriod[0] ? toISODateString(viewPeriod[0]) : '');
    searchParams.set('to', viewPeriod[1] ? toISODateString(viewPeriod[1]) : '');
    searchParams.set('filters', JSON.stringify(filterModelStatic));
    searchParams.set('sortModel', JSON.stringify(sortModelStatic));

    api.get(`/service-provider/service-reservation-confirmations/confirmed/summaries`, searchParams).then((res: any) => {
      setRows(res.data.data);
      setRowCount(res.data.total);

      setUserFilterOptions(res.filter_options.senders);
    })
      .then(() => sleep(0))
      .then(() => {
        if (apiRef?.current) {
          apiRef.current.autosizeColumns(autosizeOptions);
        }
      })
      .finally(()=>{
        setLoading(false);
      });
  }, [paginationModelStatic, filters,fastSearchValue,filterModelStatic,viewPeriod]);

  const debouncedSetPaginationModel = _.debounce(setPaginationModel, 300);

  useEffect(() => {
    fetchLogs();
  }, [
    paginationModelStatic,
    filters,
    fastSearchValue,
    filterModelStatic,
    sortModelStatic,
    viewPeriod
  ]);

  const handleChangeHeaderFilter = (column: string, value: any) => {
    setFilters((prevFilters) => {
      const updatedFilters:any = { ...prevFilters };
      if (!value) {
        delete updatedFilters[column];
      } else {
        updatedFilters[column] = value;
      }
      return updatedFilters;
    });
  };

  const [confirmingRowId, setConfirmingRowId] = useState<number | string | null>(null);

  const handleConfirmSummary = useCallback(async (row: any) => {
    if (!row?.id) return;

    setConfirmingRowId(row.id);

    try {
      await api.patch(
        `/service-provider/service-reservation-confirmations/confirmed/summaries/${row.id}`,
        {
          confirmation: 1,
        }
      );

      setRows((prev: any[]) =>
        prev.map((item: any) =>
          item.id === row.id
            ? {
              ...item,
              confirmation: true,
              confirmed_at: new Date().toISOString(),
            }
            : item
        )
      );

      // ha biztosan a backend friss adata kell:
      fetchLogs();
    } catch (error) {
      console.error(error);
    } finally {
      setConfirmingRowId(null);
    }
  }, [fetchLogs]);

  const columns: GridColDef[] = useMemo(() => {
    const columnArray: GridColDef[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        cellClassName: 'actions',
        maxWidth: 50,
        getActions: (props: any) => {
          return [<>{props?.row?.row_number}</>];
        },
        renderHeader: (props: any) => {
          return (
            <IconButton
              size={'small'}
              onClick={() => {
                setOpenHeaderFilters(!openHeaderFilters);
              }}
            >
              <FilterAltOutlined
                sx={{
                  color: '#fff',
                }}
              />
            </IconButton>
          );
        },
      },
      {
        field: 'service_provider',
        headerName: 'Service Provider',
        editable: false,
        type: 'string',
        valueGetter: (params) => {

          const staff = params.row?.staff?.name;

          return params.value?.name ? (staff ? `${params.value.name} (${staff})` : params.value.name) : null;
        },
      },
      {
        field: 'file',
        headerName: 'File',
        editable: false,
        type: 'string',
        maxWidth: 50,
        renderCell: (params) => {
          return (
            <IconButton
              size={'small'}
              href={params?.value?.link}
              target={'_blank'}
            >
              <DownloadRounded fontSize={'small'} />
            </IconButton>
          );
        },
      },
      {
        field: 'from',
        headerName: 'From',
        editable: false,
        type: 'date',
        valueGetter: (params) => params.value ? new Date(params.value) : null,
      },
      {
        field: 'to',
        headerName: 'To',
        editable: false,
        type: 'date',
        valueGetter: (params) => params.value ? new Date(params.value) : null,
      },
      {
        field: 'sender',
        headerName: 'Sender',
        editable: false,
        type: 'string',
        valueGetter: (params) => params.value?.name ? params.value.name : null,
      },
      {
        field: 'confirmation',
        headerName: 'Confirmation',
        editable: false,
        type: 'boolean',
        maxWidth: 120,
        renderCell: (params) =>{
          if (!params.row?.confirmed_at){
            return <ScheduleSendRounded color={'disabled'} fontSize={'small'} />
          }
          return params.value ? (
            <CheckRounded color={'success'} fontSize={'small'} />
          ) : (
            <ClearRounded color={'error'} fontSize={'small'} />
          )
        }
      },
      {
        field: 'created_at',
        headerName: 'Created',
        editable: false,
        type: 'dateTime',
        valueGetter: (params) => params.value ? new Date(params.value) : null,
      },

      {
        field: 'confirmed_at',
        headerName: 'Confirmed',
        editable: false,
        type: 'dateTime',
        valueGetter: (params) => params.value ? new Date(params.value) : null,
      },
      {
        field: 'message',
        headerName: 'Message',
        editable: false,
        type: 'string',
      },
    ];

    if (isAdmin) {
      columnArray.push({
        field: 'admin_confirm_action',
        headerName: 'Admin Confirm',
        editable: false,
        sortable: false,
        filterable: false,
        minWidth: 140,
        renderCell: (params) => {
          const isConfirmed = Boolean(params.row?.confirmation);
          const isLoading = confirmingRowId === params.row?.id;

          return (
            <IconButton
              size="small"
              disabled={isConfirmed || isLoading}
              onClick={() => {
                handleConfirmSummary(params.row);
              }}
              color={'success'}
            >
              <CheckRounded color={isConfirmed ? 'success' : 'primary'} />
            </IconButton>
          );
        },
      });
    }

    return columnArray;
  }, [isAdmin, confirmingRowId, handleConfirmSummary]);

  function CustomSortIconDesc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropDownRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIconAsc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIcon(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }


  const quickFilterRef = useRef<HTMLInputElement | null>(null);

  const CustomToolbar = memo(() => {
    return (
      <GridToolbarContainer
        sx={{
          gap: isMobile ? 0 : 1,
          display: openFilters ? "flex" : "none",
        }}
      >

        {!isMobile && (
          <GridToolbarFilterButton/>
        )}

        <Box sx={{flexGrow: 1}}/>

        <GridToolbarQuickFilter
          sx={{width: isMobile ? "120px" : '200px'}}
          debounceMs={400}
          inputRef={(el) => {
            if (el && !quickFilterRef.current) {
              quickFilterRef.current = el; // Megőrzi a ref-et, nem írja felül minden rendernél
            }
          }}
        />

      </GridToolbarContainer>
    );
  });

  const handleFilterModelChange = useCallback((newModel:any) => {
    setFilterModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel) ? prevModel : newModel
    );
  }, []);

  const handleSortModelChange = useCallback((newModel:any) => {
    setSortModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel) ? prevModel : newModel
    );
  }, []);

  const handlePaginationModelChange = useCallback((newModel:any) => {
    setPaginationModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel) ? prevModel : newModel
    );
  }, []);

  const MemoizedCustomToolbar = useMemo(() => CustomToolbar, [
    openFilters
  ]);


  return (
    <>
      <Header
        title={'Past Reservation Checks'}
        onChangeDateFilter={(from, to) => {
          setDateFilter([from, to]);
        }}
        rowCount={rows.length}
        refreshTableData={()=>{
          fetchLogs();
        }}
        hasPagination={false}
        showPeriodSelector={true}
      />

      <Box sx={datagridColors('calc(100vh - 145px)')}>
        <DataGridPro
          unstable_headerFilters={openHeaderFilters}
          apiRef={apiRef}
          sx={{
            pointerEvents: loading ? 'none' : 'auto',
            opacity: loading ? 0.5 : 1,
          }}
          rows={rows}
          getRowClassName={(params: any) => {
            const color =
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
            return `${color}`;
          }}
          disableColumnMenu={true}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          columnHeaderHeight={38}
          getRowHeight={(row)=> 'auto'}
          hasPagination={true}
          pagination={true}
          paginationMode={'server'}
          filterMode={'server'}
          sortingMode={'server'}
          slots={{
            // @ts-ignore
            columnSortedDescendingIcon: CustomSortIconDesc,
            columnSortedAscendingIcon: CustomSortIconAsc,
            columnUnsortedIcon: CustomSortIcon,
            filterIcon: FilterList,
            headerFilterMenu: null,
            toolbar: MemoizedCustomToolbar,
          }}
          pageSizeOptions={[25, 50, 100]}
          rowCount={rowCount}

          paginationModel={paginationModelStatic}
          filterModel={filterModelStatic}
          sortModel={sortModelStatic}

          onFilterModelChange={handleFilterModelChange}
          onSortModelChange={handleSortModelChange}
          onPaginationModelChange={handlePaginationModelChange}

        />
      </Box>
    </>
  );
};
function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
